import { ArrowLeft, Hash, Maximize2, MessagesSquare, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  useConversationReceipts,
  useMarkConversationRead,
  useTeamChatConversations,
  useTeamChatMessages,
  useTeamChatUnread,
} from '../../hooks/useTeamChat';
import type { TeamChatMessage, TeamConversation } from '../../lib/teamChatApi';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../tasks/linear/Avatar';
import { Skeleton } from '../ui/Skeleton';
import { Composer } from './Composer';
import { MessageList } from './MessageList';
import { conversationTitle } from './TeamChatSidebar';
import { UnreadBadge } from './UnreadBadge';
import { useJumpToMessage } from './jumpToMessage';
import { useModules } from '../../hooks/useModules';

/**
 * Team chat as a bubble in the bottom-right corner, on every page (BUG-10).
 *
 * The chat used to be reachable only through its sidebar entry, which people
 * had to go looking for. Here it sits where every other chat on the web sits:
 * the bubble carries the unread count and gives a short, silent shake when a
 * new message lands, and opens a compact panel — the conversation list, then
 * one conversation — over the page you are on. The full chat (/team-chat)
 * stays as it was for search, pins, members and voice; the bubble hides there.
 *
 * Mounted once in Layout, next to the bug-report panel and with the same
 * rules: fixed to the viewport, own error boundary.
 *
 * Phones (<768px) skip the panel: the bubble goes straight to /team-chat,
 * where the chat has the whole screen (a floating panel on a phone only
 * covered the page). The mobile app does the same.
 */
export function ChatWidget() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { data: unread } = useTeamChatUnread();
  const chatOn = useModules().isOn('teamChat');
  const total = unread?.total ?? 0;

  // Shake when the unread total goes UP — a new message, not a reload or a
  // conversation being read. The first value seen is the baseline.
  const [shaking, setShaking] = useState(false);
  const previousTotal = useRef<number | null>(null);
  useEffect(() => {
    const before = previousTotal.current;
    previousTotal.current = total;
    if (before === null || total <= before) return;
    setShaking(true);
    const timer = window.setTimeout(() => setShaking(false), 900);
    return () => window.clearTimeout(timer);
  }, [total]);

  // Escape closes the panel, like the bug-report one.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // The full chat is on screen there; a second one in the corner is noise.
  if (pathname.startsWith('/team-chat')) return null;
  // Chat echipă switched off for this instance (Firmio Console / Settings → Module).
  if (!chatOn) return null;

  // Higher on the AI chat, whose composer sits at the bottom of the page, and
  // hidden there on phones.
  const isAiChat = pathname.startsWith('/chat');
  const buttonBottom = isAiChat ? 'bottom-20 max-md:hidden' : 'bottom-4';
  const panelBottom = isAiChat ? 'bottom-36' : 'bottom-20';

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={t('chatWidget.title')}
          className={`fixed right-4 ${panelBottom} z-[70] flex h-[min(36rem,calc(100dvh-7rem))] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-2xl sm:w-[24rem]`}
        >
          {conversationId ? (
            <OpenConversation
              conversationId={conversationId}
              onBack={() => setConversationId(null)}
              onClose={() => setOpen(false)}
            />
          ) : (
            <ConversationPicker onPick={setConversationId} onClose={() => setOpen(false)} />
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (window.matchMedia('(max-width: 767px)').matches) {
            navigate('/team-chat');
            return;
          }
          setOpen((v) => !v);
        }}
        aria-label={open ? t('chatWidget.close') : t('chatWidget.open')}
        title={open ? t('chatWidget.close') : t('chatWidget.open')}
        aria-expanded={open}
        className={`floating-launcher fixed right-4 ${buttonBottom} z-[70] inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-fixed-white shadow-lg transition-colors hover:bg-brand-700 ${
          shaking ? 'chat-widget-shake' : ''
        }`}
      >
        {open ? <X size={20} /> : <MessagesSquare size={20} />}
        {!open && total > 0 && (
          <UnreadBadge count={total} className="absolute -right-1 -top-1 ring-2 ring-background" />
        )}
      </button>
    </>
  );
}

function PanelHeader({
  children,
  onClose,
  fullHref,
}: {
  children: React.ReactNode;
  onClose: () => void;
  fullHref: string;
}) {
  const { t } = useI18n();
  return (
    <header className="flex items-center gap-2 border-b border-border px-3 py-2.5">
      {children}
      <Link
        to={fullHref}
        onClick={onClose}
        aria-label={t('chatWidget.openFull')}
        title={t('chatWidget.openFull')}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <Maximize2 size={15} />
      </Link>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('chatWidget.close')}
        title={t('chatWidget.close')}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X size={16} />
      </button>
    </header>
  );
}

function ConversationIcon({
  conversation,
  title,
}: {
  conversation: TeamConversation;
  title: string;
}) {
  return conversation.type === 'GROUP' ? (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent">
      <Hash size={15} className="text-muted-foreground" />
    </span>
  ) : (
    <Avatar name={title || null} size={28} />
  );
}

/** Every conversation, unread first, then the most recently active. */
function ConversationPicker({
  onPick,
  onClose,
}: {
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const meId = useAuthStore((s) => s.user?.id);
  const { data: conversations, isLoading } = useTeamChatConversations();

  const sorted = useMemo(
    () =>
      (conversations ?? [])
        .slice()
        .sort(
          (a, b) =>
            Number(b.unreadCount > 0) - Number(a.unreadCount > 0) ||
            (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''),
        ),
    [conversations],
  );

  return (
    <>
      <PanelHeader onClose={onClose} fullHref="/team-chat">
        <MessagesSquare size={16} className="text-muted-foreground" />
        <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">
          {t('chatWidget.title')}
        </h2>
      </PanelHeader>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="space-y-2 p-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('chatWidget.empty')}
          </p>
        ) : (
          sorted.map((c) => {
            const title = conversationTitle(c, meId);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c.id)}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-accent"
              >
                <ConversationIcon conversation={c} title={title} />
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-sm ${
                      c.unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium'
                    }`}
                  >
                    {title}
                  </span>
                  {c.lastMessagePreview && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {c.lastMessagePreview.authorName}: {c.lastMessagePreview.snippet}
                    </span>
                  )}
                </span>
                <UnreadBadge count={c.unreadCount} />
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

/**
 * One conversation, compact: messages and composer only. Search, pins, the
 * member list and group settings stay on the full page (the ⤢ button).
 */
function OpenConversation({
  conversationId,
  onBack,
  onClose,
}: {
  conversationId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const me = useAuthStore((s) => s.user);
  const { data: conversations } = useTeamChatConversations();
  const conversation = (conversations ?? []).find((c) => c.id === conversationId) ?? null;
  const [replyTo, setReplyTo] = useState<TeamChatMessage | null>(null);

  const query = useTeamChatMessages(conversationId);
  const messages = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((page) => page.messages)
        .slice()
        .reverse(),
    [query.data],
  );
  const latestId = messages.length > 0 ? messages[messages.length - 1].id : null;
  useMarkConversationRead(conversationId, latestId);
  const { data: receipts } = useConversationReceipts(conversationId);

  const fetchOlder = useCallback(
    async () => ({ hasNextPage: (await query.fetchNextPage()).hasNextPage }),
    [query],
  );
  const { jumpTo } = useJumpToMessage(fetchOlder);

  const title = conversation ? conversationTitle(conversation, me?.id) : '';

  return (
    <>
      <PanelHeader onClose={onClose} fullHref={`/team-chat/${conversationId}`}>
        <button
          type="button"
          onClick={onBack}
          aria-label={t('chatWidget.back')}
          title={t('chatWidget.back')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={16} />
        </button>
        {conversation && <ConversationIcon conversation={conversation} title={title} />}
        <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">{title}</h2>
      </PanelHeader>
      {query.isLoading ? (
        <div className="flex-1 space-y-4 overflow-hidden px-4 py-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <MessageList
          messages={messages}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          fetchNextPage={() => void query.fetchNextPage()}
          meId={me?.id}
          isAdmin={me?.role === 'ADMIN'}
          receipts={receipts}
          isGroup={conversation?.type === 'GROUP'}
          onReply={setReplyTo}
          onJumpTo={jumpTo}
        />
      )}
      <Composer
        conversationId={conversationId}
        placeholder={t('teamChat.messagePlaceholder', { name: title })}
        replyTo={replyTo}
        onClearReply={() => setReplyTo(null)}
      />
    </>
  );
}
